<template>
	<div>
		<PageHeader
			:data="{
				name: campaign.name,
				breadcrumbs: [
					{ name: 'Home', to: '/' },
					{ name: 'Campaigns', to: '/campaigns' },
				],
			}"
		/>
		<div v-if="loadingCampaign">
			<div class="d-flex justify-content-center">
				<div class="spinner-border" role="status">
					<span class="visually-hidden">Loading...</span>
				</div>
			</div>
		</div>
		<div v-else>
			<div v-if="campaign.status == 'abborted'" class="text-center">
				<h3>Campaign is in process of cancelation of sent delivery items <font-awesome-icon :icon="['fas', 'spinner']" class="fa-spin" /></h3>
			</div>

			<!-- Campaign - PREPARING -->
			<div v-if="campaign.status == 'preparing'" class="row">
				<form @submit.prevent="updateCampaign">
					<div class="col-12">
						<div class="row mb-3">
							<!-- Main info about campaign -->
							<div class="col-md-12 mb-3">
								<div class="info-heading">
									<div class="left-info-heading mb-2">
										<div class="d-flex justify-content-between align-items-center">
											<div>
												<h2 class="p-0 m-0 d-flex justify-content-start align-items-center mb-2">
													{{ campaign.name }} <span class="badge bg-primary text-uppercase ms-2">{{ campaign.status }}</span>
												</h2>
											</div>
											<div class="div">
												<a v-if="authUser.permissions.includes('CAMPAIGN_DELETE')" @click="deletePreparedCampaign" class="btn btn-danger ms-2"
													><font-awesome-icon :icon="['fas', 'trash']" /> Delete</a
												>
											</div>
										</div>

										<p class="m-0 p-0">
											{{ campaign.description }}
											<span v-if="campaign.utm_campaign" class="text-muted small ms-1">· utm_campaign: <code>{{ campaign.utm_campaign }}</code></span>
											<span v-if="campaign.utm_content" class="text-muted small ms-1">· utm_content: <code>{{ campaign.utm_content }}</code></span>
											<button type="button" @click="enableEditMainInfo = !enableEditMainInfo" class="btn btn-sm btn-info">
												<template v-if="enableEditMainInfo">Close Edit</template>
												<template v-else>Open Edit</template>
											</button>
										</p>
									</div>
								</div>
								<div id="editMainInfo" v-if="enableEditMainInfo" class="row">
									<div class="col-4">
										<div class="form-group">
											<label for="name">Name:</label>
											<input type="text" class="form-control" v-model="campaign.name" placeholder="Name" required />
										</div>
									</div>
									<div class="col-8">
										<div class="form-group">
											<label for="name">Description:</label>
											<input type="text" class="form-control" v-model="campaign.description" placeholder="Description" />
										</div>
									</div>
									<div class="col-4 mt-2">
										<div class="form-group">
											<label for="utm_campaign">UTM Campaign:</label>
											<utm-campaign-input id="utm_campaign" v-model="campaign.utm_campaign" />
										</div>
									</div>
									<div class="col-4 mt-2">
										<div class="form-group">
											<label for="utm_content">UTM Content:</label>
											<utm-content-input id="utm_content" v-model="campaign.utm_content" />
										</div>
									</div>
								</div>
							</div>

							<!-- Unique URL tracking: postcards and custom-pattern letters -->
							<div class="col-md-12 mb-3">
								<div class="d-flex justify-content-between align-items-center mb-2">
									<div><h3>Unique URL tracking</h3></div>
									<div>
										<button v-if="authUser.permissions.includes('CAMPAIGN_UPDATE')" type="submit" class="btn btn-primary">
											Save changes
										</button>
									</div>
								</div>
								<div class="border rounded p-3">
									<div class="alert alert-info bg-info-lighter text-dark mb-2">
										💡 Every recipient gets its own short link and QR code. The short link records the visits (Response Report) and redirects to the
										destination URL with UTM params.
										<ul class="mb-0 mt-1">
											<li><strong>Postcards:</strong> use <code>{unique_url}</code> (printed link, e.g. <code>twn.link/a7k2m</code>) and <code>{unique_url_qr}</code> (QR code) in the postcard template.</li>
											<li>
												<strong>Letters:</strong> use <code v-pre>{{ $unique_url }}</code> and <code v-pre>{{ $unique_url_qr }}</code> in a
												<em>Single Item Pattern → Custom</em> letter. A default letter is one PDF for everyone, so it cannot print a link per recipient.
											</li>
										</ul>
									</div>
									<div class="form-check form-switch mb-2">
										<input class="form-check-input" type="checkbox" id="campaignUniqueUrlEnabled" v-model="campaign.unique_url_enabled" />
										<label class="form-check-label" for="campaignUniqueUrlEnabled">Generate a unique URL for every recipient</label>
									</div>
									<template v-if="campaign.unique_url_enabled">
										<div class="form-group mb-2">
											<label for="campaignUniqueUrlDomain">Destination URL</label>
											<input
												id="campaignUniqueUrlDomain"
												type="text"
												inputmode="url"
												class="form-control"
												v-model="campaign.unique_url_domain"
												placeholder="https://townweb.com"
												required
											/>
											<small class="helpText">Where the short link sends visitors; <code>utm_source</code>, <code>utm_medium</code>, <code>utm_campaign</code> and <code>utm_content</code> are added. The site needs nothing installed.</small>
										</div>
										<div class="form-group mb-2">
											<label for="campaignShortlinkDomain">Short domain</label>
											<shortlink-domain-select id="campaignShortlinkDomain" v-model="campaign.shortlink_domain" />
											<button type="button" class="btn btn-sm btn-outline-secondary mt-2" :disabled="applyingDomain" @click="applyDomainToUnsent">
												<i :class="['fad', 'me-1', applyingDomain ? 'fa-spinner fa-spin' : 'fa-sync']"></i> Apply to recipients not sent yet
											</button>
											<small class="helpText d-block">Saves the campaign and moves links of recipients that were not mailed yet to this domain.</small>
										</div>
										<div class="form-check form-switch mb-2">
											<input class="form-check-input" type="checkbox" id="campaignShortlinkSlack" v-model="campaign.shortlink_slack_notify" />
											<label class="form-check-label" for="campaignShortlinkSlack">Slack notification on every visit</label>
											<small class="helpText d-block">Posts to the channel set in Settings → Short Links. Can also be switched in the Response Report.</small>
										</div>
									</template>
									<div v-if="campaignPrintsShortLink && !campaign.unique_url_enabled" class="alert alert-warning bg-warning-lighter text-dark mt-2 mb-0">
										The {{ campaign.type === 'letter' ? 'letter pattern' : 'postcard template' }} prints the short link / QR code; turn on unique URL
										tracking before sending.
									</div>
									<div
										v-if="campaign.unique_url_enabled && campaign.type === 'letter' && !(campaign.letter_type === 'single-item-pattern' && campaign.letter_pattern === 'custom')"
										class="alert alert-warning bg-warning-lighter text-dark mt-2 mb-0"
									>
										This letter uses one shared PDF, so recipients get no printed link. Switch to <em>Single Item Pattern → Custom</em> and add
										<code v-pre>{{ $unique_url }}</code> / <code v-pre>{{ $unique_url_qr }}</code>.
									</div>
								</div>
							</div>

							<!-- Delivery Type Info -->
							<div class="col-md-12 mb-3">
								<div class="d-flex justify-content-between align-items-center mb-2">
									<div><h3>Delivery Type Info</h3></div>
									<div>
										<button v-if="authUser.permissions.includes('CAMPAIGN_UPDATE')" type="submit" class="btn btn-primary">
											Save changes
										</button>
									</div>
								</div>
								<div class="alert alert-warning bg-warning-lighter text-dark">
									💡 In this section, you need to set up all settings related to the type of delivery (letter or postcard). All settings are stored in the
									database and can be updated before sending the campaign. Some of them are required.
								</div>
								<div class="row">
									<!-- Letter Settings -->
									<div class="col-md-6">
										<div class="letter border rounded p-3">
											<div class="d-flex justify-content-between align-items-center">
												<div class="form-check">
													<input class="form-check-input" name="type" id="letter" type="radio" v-model="campaign.type" :value="'letter'" />
													<label class="form-check-label" for="letter">
														Letter
													</label>
												</div>
											</div>
											<div class="letter-settings">
												<div v-if="campaign.type != 'letter'" class="letter-settings-overlay"></div>
												<div class="letter-lob-settings row mt-3">
													<div class="col-md-12">
														<h5>Letter LOB Settings</h5>
													</div>
													<div class="col-md-6 mb-2">
														<div class="form-group">
															<label for="name">Campaign Sender</label>
															<select class="form-select" v-model="campaign.sender" :required="campaign.type == 'letter'">
																<option :key="index" v-for="(sender, index) in senders" :value="sender.name">{{ sender.name }}</option>
															</select>
														</div>
													</div>
													<div class="col-md-6 mb-2">
														<div class="form-group">
															<label for="letter_color">Color</label>
															<select class="form-select" v-model="campaign.letter_color" :required="campaign.type == 'letter'">
																<option value="1">Yes</option>
																<option value="0">No</option>
															</select>
														</div>
													</div>
													<div class="col-md-6 mb-2">
														<div class="form-group">
															<label for="letter_double_sided">Double sided</label>
															<select class="form-select" v-model="campaign.letter_double_sided" :required="campaign.type == 'letter'">
																<option value="1">Yes</option>
																<option value="0">No</option>
															</select>
														</div>
													</div>
													<div class="col-md-6 mb-2">
														<div class="form-group">
															<label for="extra_service">Extra service</label>
															<select class="form-select" v-model="campaign.letter_extra_service">
																<option value="">None</option>
																<option value="certified">Certified</option>
																<option value="certified_return_receipt">Certified return receipt</option>
															</select>
														</div>
													</div>
													<div class="col-md-12 mb-2">
														<div class="form-group">
															<label for="letter_address_placement">Address placement</label>
															<select class="form-select" v-model="campaign.letter_address_placement" :required="campaign.type == 'letter'">
																<option value="top_first_page">On top of first page</option>
																<option value="insert_blank_page">On a blank page</option>
															</select>
														</div>
													</div>
												</div>
												<div class="letter-content-settings row mt-3">
													<h5>Letter Content Settings</h5>
													<div class="col-md-8 mb-2">
														<label for="">Letter Type</label>
														<select class="form-select" v-model="campaign.letter_type">
															<option value="default">Default</option>
															<option value="single-item-pattern">Single Item Pattern</option>
														</select>
													</div>
													<div class="col-md-4 mb-2">
														<label for="">Letter Pages Count</label>
														<input type="number" class="form-control" v-model="campaign.letter_pages_count" />
														<small class="helpText">💡 Just for calculation</small>
													</div>
													<div v-if="campaign.letter_type == 'default'" class="col-md-12">
														<div class="alert alert-info bg-info-lighter text-dark mb-2">
															💡 Default Letter Type: Each letter shares the same uploaded PDF, with only the address differing for each item.
														</div>
														<div class="border bg-light rounded p-3">
															<label for="letter_pdf">Letter PDF</label>
															<input
																v-if="!campaign.letter_pdf"
																type="file"
																@change="campaignUploads($event, 'letter_pdf')"
																name="letter-pdf"
																class="form-control"
															/>
															<h4 v-if="campaign.letter_pdf && !uploadLoading">
																<font-awesome-icon :icon="['fas', 'file-pdf']" />
																<a :href="storageUrl + campaign.letter_pdf" class="ms-2" target="_blank">View campaign letter</a>
																<a class="btn btn-danger ms-3" @click="removeUpload('letter_pdf')"><font-awesome-icon :icon="['fas', 'trash']"/></a>
															</h4>
														</div>
													</div>
													<div v-if="campaign.letter_type == 'single-item-pattern'" class="col-md-12 mb-2">
														<div class="alert alert-info bg-info-lighter text-dark mb-2">
															💡 Single Item Pattern: Each letter generates a unique PDF with a selected pattern. Currently, there is only an option
															for creating a custom pattern (with different object data). However, in the future, there may be predefined patterns
															available for use.
														</div>
														<div class="border bg-light rounded p-3 d-flex justify-content-between align-items-center">
															<select class="form-select w-50" v-model="campaign.letter_pattern">
																<option value="">Select</option>
																<option value="custom">Custom Pattern</option>
															</select>
															<button
																type="button"
																class="btn btn-primary w-25"
																:disabled="campaign.letter_pattern !== 'custom'"
																data-bs-toggle="modal"
																data-bs-target="#campaignLetterPatternHtmlModal"
															>
																<font-awesome-icon :icon="['fab', 'html5']" /> Pattern
															</button>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
									<!-- Postcard Settings -->
									<div class="col-md-6">
										<div class="postcard border rounded p-3">
											<div class="d-flex justify-content-between align-items-center">
												<div class="form-check">
													<input class="form-check-input" name="type" id="postcard" type="radio" v-model="campaign.type" :value="'postcard'" />
													<label class="form-check-label" for="postcard">
														Postcard
													</label>
												</div>
											</div>
											<div class="postcard-settings">
												<div v-if="campaign.type != 'postcard'" class="postcard-settings-overlay"></div>
												<div class="postcard-lob-settings row mt-3">
													<div class="col-md-12">
														<h5>Postcard LOB settings</h5>
													</div>
													<div class="col-md-6 mb-2">
														<div class="form-group">
															<label for="name">Size</label>
															<select class="form-select" v-model="campaign.postcard_size" :required="campaign.type == 'postcard'">
																<option value="">Select</option>
																<option value="4x6">4x6</option>
																<option value="6x9">6x9</option>
																<option value="6x11">6x11</option>
															</select>
														</div>
													</div>
													<div class="col-md-6 mb-2">
														<div class="form-group">
															<label for="name">Campaign Sender</label>
															<select class="form-select" v-model="campaign.sender" :required="campaign.type == 'postcard'">
																<option :key="index" v-for="(sender, index) in senders" :value="sender.name">{{ sender.name }}</option>
															</select>
														</div>
													</div>
												</div>
												<div class="postcard-content-settings row mt-3">
													<div class="col-md-12">
														<h5>Postcard Content Settings</h5>
													</div>
													<div class="col-md-6 mb-2">
														<div class="form-group">
															<label for="name">Postcard Front</label>
															<img v-if="campaign.postcard_front" :src="storageUrl + campaign.postcard_front" class="w-100" />
															<div v-if="!campaign.postcard_front && uploadLoading" class="d-flex justify-content-center">
																<div class="spinner-grow" role="status">
																	<span class="visually-hidden">Loading...</span>
																</div>
															</div>
															<input
																v-if="!campaign.postcard_front"
																type="file"
																@change="campaignUploads($event, 'postcard_front')"
																name="postcard-front"
																class="form-control"
															/>
															<a v-if="campaign.postcard_front" class="btn btn-danger w-100" @click="removeUpload('postcard_front')">Remove</a>
															<button v-if="campaign.postcard_front" type="button" class="btn btn-outline-secondary w-100 mt-2" @click="openPatternEditor('front')">
																<i class="fad fa-cog me-1"></i> Template ({{ postcardPatterns.front.length }})
															</button>
														</div>
													</div>

													<div class="col-md-6 mb-2">
														<div class="form-group">
															<label for="name">Postcard Back</label>
															<img v-if="campaign.postcard_back && !uploadLoading" :src="storageUrl + campaign.postcard_back" class="w-100" />
															<div v-if="!campaign.postcard_back && uploadLoading" class="d-flex justify-content-center">
																<div class="spinner-grow" role="status">
																	<span class="visually-hidden">Loading...</span>
																</div>
															</div>
															<input
																v-if="!campaign.postcard_back"
																type="file"
																@change="campaignUploads($event, 'postcard_back')"
																name="postcard-back"
																class="form-control"
															/>
															<a v-if="campaign.postcard_back" class="btn btn-danger w-100" @click="removeUpload('postcard_back')">Remove</a>
															<button v-if="campaign.postcard_back" type="button" class="btn btn-outline-secondary w-100 mt-2" @click="openPatternEditor('back')">
																<i class="fad fa-cog me-1"></i> Template ({{ postcardPatterns.back.length }})
															</button>
															</div>
															</div>
															</div>
															<div v-if="campaign.postcard_front && campaign.postcard_back" class="row">
																<div class="col-md-12">
																	<button type="button" class="btn btn-primary w-100" :disabled="templatePreview.loading" @click="previewTemplate">
																		<i :class="['fad', 'me-1', templatePreview.loading ? 'fa-spinner fa-spin' : 'fa-eye']"></i> Preview postcard
																	</button>
																	<small class="helpText">Rendered by the server exactly as it will print: the first recipient's data, the QR code and every template field.</small>
																</div>
															</div>
											</div>
										</div>
									</div>
								</div>
							</div>

							<!-- Delivery Objects -->
							<div class="col-md-12 mb-3">
								<div class="d-flex justify-content-between align-items-center mb-2">
									<div><h3>Data Info</h3></div>
									<div></div>
								</div>
								<div class="alert alert-warning bg-warning-lighter text-dark">
									💡 If you need to check, update, delete, or add a Delivery Object, you can do so in this section. In the latest version, we had some
									limitations, but now we've managed to allow the addition of anything in the Delivery Object (municipality, client, business, or even a random
									Delivery Object with a valid address in the US). Hopefully, this will make the process of working with campaigns easier.
								</div>
								<div class="delivery-objects">
									<div class="d-flex justify-content-between align-items-center">
										<div>
											<div class="d-flex justify-content-between align-items-center mb-2">
												<div>
													<small
														>Initial Source: <span class="text-capitalize">{{ campaign.source }}</span></small
													>
													<h4 class="mb-2 d-flex justify-content-start align-items-center">
														Delivery Objects <span class="badge bg-primary ms-2">{{ campaign.municipalities_count }}</span>
													</h4>
												</div>

												<div>
													<p>
														Applied filters:
														<span :key="filter" v-for="filter in printingFilters" class="badge badge-primary bg-primary me-1">{{ filter }}</span>
													</p>
												</div>
											</div>
											<div class="alert alert-warning bg-info-light mb-3">
												💡 "Delivery objects" can refer to municipalities or clients (or, in the future, business objects or custom objects). These objects
												represent a single address to which a letter or postcard is sent. The data within a Delivery object (city, address, zip code, state)
												is intended for one-time use only. Any modifications to this data will not affect the corresponding entries in the municipality or
												client tables. Such changes can be managed through specific actions.
											</div>
										</div>
									</div>
									<div class="table-actions d-flex justify-content-between align-items-center">
										<div class="d-flex justify-content-start align-items-center position-relative">
											<input
												type="text"
												v-model="deliveryObjectSearch"
												@input="getDeliveryObjects()"
												class="form-control"
												style="width:200px"
												placeholder="Search..."
											/>
											<span v-if="deliveryObjectSearch != ''" @click="clearSearch()" style="cursor:pointer;position:absolute;right:60px">x</span>
											<span title="Search here is only for checking is Delivery Object in list. Search is triggered on enter" class="me-2 btn">💡</span>
										</div>
										<div class="actions">
											<button type="button" @click="addDeliveryObject()" class="btn btn-primary btn-sm">
												<font-awesome-icon :icon="['fas', 'plus-circle']" /> Add Delivery Object
											</button>
										</div>
									</div>
									<table :class="!loading && deliveryObjects.length > 0 ? 'table table-hover' : 'table table-hover loading'">
										<thead>
											<tr>
												<th class="text-center">ID</th>
												<th>Name</th>
												<th>City</th>
												<th>Address</th>
												<th>Zip</th>
												<th class="text-center">State</th>
												<th class="text-center">Actions</th>
											</tr>
										</thead>
										<tbody>
											<template v-for="dObject in deliveryObjects">
												<tr
													:key="'edit-' + dObject.id"
													:class="{
														'bg-danger-light':
															(dObject.city == '' || dObject.city == null) &&
															(dObject.address == '' || dObject.address == null) &&
															(dObject.zip == '' || dObject.zip == null),
														'bg-warning-light':
															dObject.city != '' &&
															dObject.city != null &&
															(dObject.address == '' || dObject.address == null) &&
															(dObject.zip == '' || dObject.zip == null),
														'bg-warning-lighter':
															(dObject.city == '' || dObject.city == null) &&
															dObject.address != '' &&
															dObject.address != null &&
															dObject.zip != '' &&
															dObject.zip != null,
													}"
												>
													<td class="text-center align-middle">
														{{ dObject.id }}
													</td>
													<td class="align-middle">
														<h6 class="p-0 m-0 d-flex justify-content-start align-items-center">
															{{ dObject.name }}
															<span
																v-if="
																	(dObject.city == '' || dObject.city == null) &&
																		(dObject.address == '' || dObject.address == null) &&
																		(dObject.zip == '' || dObject.zip == null)
																"
																class="btn btn-sm ms-2"
																title="A lot of data is missing for this Delivery Object. The delivery will not be sent to this delivery object for sure."
															>
																<font-awesome-icon :class="['text-danger fs-4']" :icon="['fas', 'exclamation-triangle']" />
															</span>
															<span
																class="btn btn-sm ms-2"
																title="Important data is missing for this Object Delivery. The delivery will not be sent to this delivery object for sure."
																v-else-if="(dObject.address == '' || dObject.address == null) && (dObject.zip == '' || dObject.zip == null)"
															>
																<font-awesome-icon :class="['text-warning fs-4 ms-2']" :icon="['fas', 'exclamation-circle']" />
															</span>
															<span
																class="btn btn-sm ms-2"
																title="The city is missing; it's nice to have but not mandatory if all other data is available."
																v-else-if="dObject.city == '' || dObject.city == null"
															>
																<font-awesome-icon :class="['text-warning fs-6 ms-2']" :icon="['fas', 'exclamation']" />
															</span>
														</h6>
													</td>
													<td class="align-middle">{{ dObject.city }}</td>
													<td class="align-middle">{{ dObject.address }}</td>
													<td class="align-middle">{{ dObject.zip }}</td>
													<td class="text-center align-middle">{{ dObject.state }}</td>
													<td class="text-center align-middle">
														<a role="button" @click="editDeliveryObject(dObject)" class="btn btn-primary btn-sm rounded-circle me-1">
															<font-awesome-icon :icon="['fas', 'pen']" />
														</a>
														<a role="button" @click="deleteDeliveryObject(dObject.id)" class="btn btn-danger btn-sm rounded-circle">
															<font-awesome-icon :icon="['fas', 'trash']" />
														</a>
													</td>
												</tr>
											</template>
										</tbody>
									</table>
									<div v-if="loading && deliveryObjects.length === 0" class="d-flex justify-content-center">
										<div class="spinner-border" role="status">
											<span class="visually-hidden">Loading...</span>
										</div>
									</div>
									<LPDataTablePagination :pagination="pagination" v-on:getItems="getItems($event.page)" />
								</div>
							</div>

							<!-- Sending Info -->
							<div class="col-md-12 mb-3">
								<div class="d-flex justify-content-between align-items-center mb-2">
									<div><h3>Sending Info</h3></div>
									<div>
										<button v-if="authUser.permissions.includes('CAMPAIGN_UPDATE')" type="submit" class="btn btn-primary">
											Save changes
										</button>
									</div>
								</div>
								<div class="alert alert-warning bg-warning-lighter text-dark">
									💡 Here, we need to highlight some important points regarding limitations and the process of sending. Firstly, LOB (mailing provider) imposes a
									limitation on canceling items within a 20-minute window. This means that if a letter is sent at 08:40, the cancellation window is only open
									until 09:00. In the case of large campaigns, this timeframe might not be sufficient, so it is crucial to ensure 100% accuracy before initiating
									the campaign. If you have any doubts or concerns about sending, please feel free to contact Srdjan at any time.
								</div>

								<div class="row">
									<!-- Test Mode Settings -->
									<div class="col-md-3">
										<div class="test-mode border rounded p-3">
											<div class="d-flex justify-content-between align-items-center">
												<div class="form-check">
													<input class="form-check-input" name="live" id="testMode" type="radio" v-model="campaign.live" :value="0" />
													<label class="form-check-label fw-bold" for="testMode">
														Test Mode
													</label>
												</div>
											</div>
											<div class="test-mode-settings">
												<div class="alert alert-info bg-info-lighter text-dark">
													💡 Testing Mode is a convenient place to test campaigns. If we want to determine the actual number of items that will be sent
													out, we can initiate a test here by selecting Testing Mode.
												</div>
												<div v-if="campaign.live" class="test-mode-settings-overlay"></div>
												<div class="row mt-3">
													<div class="col-md-12">
														<h5>Test Mode Settings</h5>
													</div>
													<div class="col-md-12 mb-2">
														<div class="form-group">
															<label for="name">Mail type</label>
															<select class="form-select" v-model="campaign.mail_type" :required="!campaign.live">
																<option value="usps_first_class">USPS first class</option>
																<option value="usps_standard">USPS standard</option>
															</select>
														</div>
													</div>
													<div class="col-md-12">
														<button
															type="button"
															class="btn btn-success rounded float-end ms-2"
															:disabled="sendButtonDisable()"
															@click="openLiveModal"
															v-if="authUser.permissions.includes('CAMPAIGN_CREATE')"
														>
															<font-awesome-icon :icon="['fas', 'shipping-fast']" />
															Send TEST campaign
														</button>
													</div>
												</div>
											</div>
										</div>
									</div>
									<!-- Live Mode Settings -->
									<div class="col-md-9">
										<div class="test-mode border rounded p-3">
											<div class="d-flex justify-content-between align-items-center">
												<div class="form-check">
													<input class="form-check-input" name="live" id="liveMode" type="radio" v-model="campaign.live" :value="1" />
													<label class="form-check-label fw-bold" for="liveMode">
														Live Mode
													</label>
												</div>
											</div>
											<div class="live-mode-settings">
												<div class="alert alert-warning bg-warning text-dark">
													🙏 Double-check to ensure everything is set up as it needs to be. From this point, there isn't much we can do if something goes
													wrong (just kidding – we actually have some safety options), but we need to be really careful. After clicking "Send Campaign,"
													we will first send a test Delivery Object from the list to provide you with a preview of how it will truly appear to the
													recipient. If everything is okay, then upon confirming the campaign, the sending process will commence. Now, we've added a panic
													button. If you need to stop the campaign, the panic button will halt it. Of course, it will still send out some deliveries, but
													not all, minimizing potential damage. After pressing the panic button, all processes will cease, and the system will attempt to
													cancel all sent-out deliveries within the 20-minute window (those beyond this window cannot be canceled, but this minimizes the
													damage).
												</div>
												<div v-if="!campaign.live" class="live-mode-settings-overlay"></div>
												<div class="row mt-3">
													<div class="col-md-12">
														<h5>Live Mode Settings</h5>
													</div>
													<div class="col-md-12 mb-2">
														<div class="form-group">
															<label for="name">Mail type</label>
															<select class="form-select" v-model="campaign.mail_type" :required="campaign.live">
																<option value="usps_first_class">USPS first class</option>
																<option value="usps_standard">USPS standard</option>
															</select>
														</div>
													</div>
													<!-- <div class="col-md-12 mb-2">
													<div class="form-group mb-3">
														<label for="send_at">Send date <small>(For schedule)</small></label>
														<input type="datetime-local" class="form-control" v-model="campaign.send_at" />
													</div>
													<div class="alert alert-info bg-info-lighter text-dark">
														💡 Scheduled campaigns will run at the selected date and time. In the datetime picker, please choose a rounded time in full
														hours. For example, 03/17/2027, 06:00 PM.
													</div>
												</div> -->
													<div class="col-md-12 mb-2">
														<div class="alert alert-info bg-info-lighter text-dark">
															💡 The campaign will be sent in one hour.
														</div>
													</div>
													<div class="col-md-12">
														<button
															type="button"
															class="btn btn-success btn-lg rounded float-end ms-2"
															:disabled="sendButtonDisable()"
															@click="openLiveModal"
															v-if="authUser.permissions.includes('CAMPAIGN_CREATE')"
														>
															<font-awesome-icon :icon="['fas', 'shipping-fast']" />
															Send campaign
														</button>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</form>
			</div>
			<!-- #Campaign - PREPARING -->

			<!-- Campaing - SENT -->
			<div v-if="campaign.status == 'sent' || campaign.status == 'canceled'" class="row">
				<div class="col-md-12">
					<div class="info-heading">
						<div class="left-info-heading mb-2">
							<div class="d-flex justify-content-between align-items-center">
								<div>
									<h2 class="p-0 m-0 d-flex justify-content-start align-items-center mb-2">
										{{ campaign.name }} <span v-if="campaign.status == 'sent'" class="badge bg-success text-uppercase ms-2">{{ campaign.status }}</span>
										<span v-if="campaign.status == 'canceled'" class="badge bg-warning text-uppercase ms-2">{{ campaign.status }}</span>
										<div v-if="campaignCancelLoading" class="spinner-border" role="status">
											<span class="visually-hidden">Loading...</span>
										</div>
									</h2>
								</div>
								<div class="div">
									<router-link
										v-if="campaign.unique_url_enabled"
										:to="{ name: 'campaignResponseReport', params: { campaignId: campaign.id } }"
										class="btn btn-outline-primary float-end ms-2"
									>
										<i class="fad fa-qrcode me-1"></i> Response Report
									</router-link>
									<button @click="deleteSentCampaign" class="btn btn-danger float-end ms-2">
										<font-awesome-icon :icon="['fas', 'trash']" /> Delete campaign
									</button>
									<button v-if="campaign.status !== 'canceled' && campaign.status !== 'scheduled'" @click="cancelCampaign" class="btn btn-warning float-end">
										<template v-if="!campaignCancelLoading">
											Cancel campaign
										</template>
										<template v-if="campaignCancelLoading">
											<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
											Canceling...
										</template>
									</button>
									<button v-if="campaign.status === 'scheduled'" class="btn btn-warning float-end ms-2" @click="cancelSchedule">
										<font-awesome-icon :icon="['fas', 'times']" /> Cancel schedule
									</button>
								</div>
							</div>

							<p class="m-0 p-0">
								{{ campaign.description }}
							</p>
							<!-- UTM campaign stays editable after sending: the marketing tracker matches on it -->
							<div class="d-flex align-items-start gap-2 mt-2">
								<template v-if="!utmEdit.open">
									<span class="text-muted small">UTM campaign:</span>
									<code v-if="campaign.utm_campaign" class="small">{{ campaign.utm_campaign }}</code>
									<span v-else class="text-muted small">not set</span>
									<span class="text-muted small ms-2">UTM content:</span>
									<code v-if="campaign.utm_content" class="small">{{ campaign.utm_content }}</code>
									<span v-else class="text-muted small">not set</span>
									<button type="button" class="btn btn-sm btn-link p-0 small" @click="openUtmEdit">Edit</button>
								</template>
								<template v-else>
									<div style="max-width: 420px" class="flex-grow-1">
										<utm-campaign-input v-model="utmEdit.value" />
									</div>
									<div style="max-width: 320px" class="flex-grow-1">
										<utm-content-input v-model="utmEdit.content" />
									</div>
									<button type="button" class="btn btn-sm btn-primary" :disabled="utmEdit.saving" @click="saveUtmCampaign">Save</button>
									<button type="button" class="btn btn-sm btn-light" :disabled="utmEdit.saving" @click="utmEdit.open = false">Cancel</button>
								</template>
							</div>
						</div>
					</div>
					<div v-if="campaign.status == 'sent'" class="alert alert-success bg-success-lighter text-dark">
						🚚 Campaign "{{ campaign.name }}" has been <span class="text-uppercase fw-bold">sent</span>. Below, you can find the status of the campaign, including the
						number of deliveries sent, the count of errors (along with error descriptions), and much more that may be interesting regarding the campaign.
					</div>
					<div v-if="campaign.status == 'canceled'" class="alert alert-warning bg-warning-lighter text-dark">
						✋ Campaign "{{ campaign.name }}" has been <span class="text-uppercase fw-bold">canceled</span>. There is a possibility that not all deliveries were
						canceled due to a 20-minute time window for cancellations. The actual delivery data is displayed in the table below.
					</div>
				</div>
				<!-- Delivery Objects -->
				<div class="col-md-12 mb-3">
					<div class="delivery-objects">
						<div class="d-flex justify-content-between align-items-center">
							<div>
								<div class="d-flex justify-content-between align-items-center mb-2">
									<div>
										<small
											>Initial Source: <span class="text-capitalize">{{ campaign.source }}</span></small
										>
										<h4 class="mb-2 d-flex justify-content-start align-items-center">
											Deliveries
											<span class="badge bg-primary ms-2">
												<template v-if="!objectsCountLoading">{{ deliveryObjectsCount }}</template>
												<font-awesome-icon v-else :icon="['fas', 'spinner']" class="fa-spin" />
											</span>
										</h4>
									</div>

									<div>
										<p>
											Applied filters:
											<span :key="filter" v-for="filter in printingFilters" class="badge badge-primary bg-primary me-1">{{ filter }}</span>
										</p>
									</div>
								</div>
								<div class="alert alert-warning bg-info-light mb-3">
									💡 "Delivery" represents the sent instance of the Delivery Object. It is a response from the Delivery Object that is transmitted to the "LOB".
									Each delivery is associated with a Delivery Object and includes the status of that object. The status encompasses information returned from the
									"LOB," including the sent date, expected delivery, errors, and more.
								</div>
							</div>
						</div>
						<div class="table-actions d-flex justify-content-between align-items-center">
							<div class="d-flex justify-content-start align-items-center">
								<input type="text" v-model="deliveryObjectSearch" @change="getDeliveryObjects()" class="form-control" style="width:200px" placeholder="Search..." />
								<span title="Search here is only for checking is Delivery Object in list. Search is triggered on enter" class="me-2 btn">💡</span>
								<div class="stats">
									Sent:
									<span class="badge badge-primary">
										<template v-if="!objectsCountLoading && !errorsCountLoading && !canceledCountLoading">{{
											deliveryObjectsCount - deliveryErrorsCount - deliveryCanceledCount
										}}</template>
										<font-awesome-icon v-else :icon="['fas', 'spinner']" class="fa-spin" />
									</span>
								</div>
								<div class="stats ms-3">
									Errors :
									<span class="badge badge-danger">
										<template v-if="!errorsCountLoading">{{ deliveryErrorsCount }}</template>
										<font-awesome-icon v-else :icon="['fas', 'spinner']" class="fa-spin" />
									</span>
								</div>
								<div class="stats ms-3">
									Canceled:
									<span class="badge badge-warning">
										<template v-if="!canceledCountLoading">{{ deliveryCanceledCount }}</template>
										<font-awesome-icon v-else :icon="['fas', 'spinner']" class="fa-spin" />
									</span>
								</div>
								<div class="stats ms-3">
									Estimated costs: <span class="badge badge-success">${{ costsEstimated }}</span>
								</div>
							</div>
							<div class="actions">
								<div class="form-group">
									<select class="form-select form-select-sm" v-model="statusFilter" @change="getDeliveryObjects()">
										<option value="">All</option>
										<option value="sent">Sent</option>
										<option value="canceled">Canceled</option>
										<option value="error">Error</option>
									</select>
								</div>
							</div>
						</div>
						<table :class="!loading && deliveryObjects.length > 0 ? 'table table-hover' : 'table table-hover loading'">
							<thead>
								<tr>
									<th class="text-center">ID</th>
									<th>Name</th>
									<th>City</th>
									<th>Address</th>
									<th>Zip</th>
									<th class="text-center">State</th>
									<th class="text-center">Sent At</th>
									<th class="text-center">Status</th>
									<th v-if="campaign.unique_url_enabled" class="text-center">Visited</th>
									<th class="text-center">Actions</th>
								</tr>
							</thead>
							<tbody>
								<template v-for="dObject in deliveryObjects">
									<tr
										:key="'edit-' + dObject.id"
										:class="{
											'bg-danger-light':
												(dObject.city == '' || dObject.city == null) &&
												(dObject.address == '' || dObject.address == null) &&
												(dObject.zip == '' || dObject.zip == null),
											'bg-warning-light':
												dObject.city != '' &&
												dObject.city != null &&
												(dObject.address == '' || dObject.address == null) &&
												(dObject.zip == '' || dObject.zip == null),
											'bg-warning-lighter':
												(dObject.city == '' || dObject.city == null) &&
												dObject.address != '' &&
												dObject.address != null &&
												dObject.zip != '' &&
												dObject.zip != null,
										}"
									>
										<td class="text-center align-middle">
											{{ dObject.id }}
										</td>
										<td class="align-middle">
											<h6 class="p-0 m-0 d-flex justify-content-start align-items-center">
												{{ dObject.name }}
												<span
													v-if="
														(dObject.city == '' || dObject.city == null) &&
															(dObject.address == '' || dObject.address == null) &&
															(dObject.zip == '' || dObject.zip == null)
													"
													class="btn btn-sm ms-2"
													title="A lot of data is missing for this Delivery Object. The delivery will not be sent to this delivery object for sure."
												>
													<font-awesome-icon :class="['text-danger fs-4']" :icon="['fas', 'exclamation-triangle']" />
												</span>
												<span
													class="btn btn-sm ms-2"
													title="Important data is missing for this Object Delivery. The delivery will not be sent to this delivery object for sure."
													v-else-if="(dObject.address == '' || dObject.address == null) && (dObject.zip == '' || dObject.zip == null)"
												>
													<font-awesome-icon :class="['text-warning fs-4 ms-2']" :icon="['fas', 'exclamation-circle']" />
												</span>
												<span
													class="btn btn-sm ms-2"
													title="The city is missing; it's nice to have but not mandatory if all other data is available."
													v-else-if="dObject.city == '' || dObject.city == null"
												>
													<font-awesome-icon :class="['text-warning fs-6 ms-2']" :icon="['fas', 'exclamation']" />
												</span>
											</h6>
										</td>
										<td class="align-middle">{{ dObject.city }}</td>
										<td class="align-middle">{{ dObject.address }}</td>
										<td class="align-middle">{{ dObject.zip }}</td>
										<td class="text-center align-middle">{{ dObject.state }}</td>
										<td class="text-center align-middle">{{ dObject.send_at }}</td>
										<td class="text-center align-middle">
											<span v-if="dObject.delivery && dObject.delivery.status === 'sent'" class="badge badge-success bg-success">{{
												dObject.delivery.status
											}}</span>
											<span v-if="dObject.delivery && dObject.delivery.status === 'canceled'" class="badge badge-warning bg-warning">{{
												dObject.delivery.status
											}}</span>
											<span
												role="button"
												:title="dObject.delivery.message"
												v-if="dObject.delivery && dObject.delivery.status == 'error'"
												class="badge badge-danger bg-danger"
												>{{ dObject.delivery.status }}</span
											>
											<span v-if="!dObject.delivery" class="badge badge-warning bg-warning">canceled</span>
											</td>
											<td v-if="campaign.unique_url_enabled" class="text-center align-middle">
												<button
													v-if="dObject.unique_url_visited_at"
													type="button"
													class="btn btn-link btn-sm p-0 text-success small text-decoration-none"
													:title="`${dObject.unique_url} — show visit timeline`"
													@click="openVisitTimeline(dObject)"
												>
													<i class="fad fa-check-circle"></i> {{ formatVisit(dObject) }}
												</button>
												<span v-else-if="dObject.unique_url" class="text-muted small" :title="dObject.unique_url">Not visited</span>
												<span v-else class="text-muted small">—</span>
											</td>
										<td class="text-center align-middle">
											<button
												v-if="dObject.delivery && dObject.delivery.status !== 'canceled' && dObject.delivery.status !== 'error'"
												:disabled="!checkCancelable(dObject.delivery)"
												@click="cancelDelivery(dObject.delivery)"
												class="btn btn-warning btn-sm"
											>
												<template v-if="dObject.delivery && dObject.delivery.status == 'sent'">
													Cancel
												</template>

												<template v-if="dObject.delivery && dObject.delivery.status == 'canceling'">
													<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
													Canceling...
												</template>
											</button>
										</td>
									</tr>
								</template>
							</tbody>
						</table>
						<div v-if="loading && deliveryObjects.length === 0" class="d-flex justify-content-center">
							<div class="spinner-border" role="status">
								<span class="visually-hidden">Loading...</span>
							</div>
						</div>
						<LPDataTablePagination :pagination="pagination" v-on:getItems="getItems($event.page)" />
						<div class="alert alert-warning bg-warning-lighter text-dark mb-3 mt-2">
							💡 If the status is an error, you can hover over the error text to obtain more information about the error and understand why the error occurred.
						</div>
					</div>
				</div>
			</div>
			<!-- #Campaign - SENT -->
		</div>

		<div class="modal fade" id="postcardPreviewModal" tabindex="-1" aria-labelledby="postcardPreviewModalLabel" aria-hidden="true">
			<div class="modal-dialog modal-xl modal-dialog-centered">
				<div class="modal-content">
					<div class="modal-header">
						<div>
							<h5 class="modal-title" id="postcardPreviewModalLabel">Postcard preview</h5>
							<small v-if="templatePreview.sample.name" class="text-muted">
								Recipient: {{ templatePreview.sample.name }}<template v-if="templatePreview.sample.unique_url"> · link <code>{{ templatePreview.sample.unique_url }}</code></template>
							</small>
						</div>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div v-if="templatePreview.loading" class="d-flex justify-content-center align-items-center py-5">
							<div class="spinner-border me-3" role="status"><span class="visually-hidden">Loading...</span></div>
							Rendering postcard...
						</div>
						<div v-else-if="templatePreview.error" class="alert alert-danger mb-0">{{ templatePreview.error }}</div>
						<div v-else class="row g-3">
							<div v-for="(image, index) in templatePreview.images" :key="image" class="col-lg-6">
								<div class="small text-muted mb-1">{{ index === 0 ? 'Front' : 'Back' }}</div>
								<a :href="image" target="_blank" rel="noopener"><img :src="image" class="w-100 border rounded" :alt="index === 0 ? 'Postcard front preview' : 'Postcard back preview'"/></a>
							</div>
							<div v-if="templateUsesUniqueUrl" class="col-12">
								<small class="text-muted">The token <code>a7k2m</code> is a placeholder; every recipient gets its own link when the campaign is sent.</small>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="modal fade" id="visitTimelineModal" tabindex="-1" aria-labelledby="visitTimelineModalLabel" aria-hidden="true">
			<div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
				<div class="modal-content">
					<div class="modal-header">
						<div>
							<h5 class="modal-title" id="visitTimelineModalLabel">Visit timeline</h5>
							<small v-if="visitTimelineObject" class="text-muted">
								{{ visitTimelineObject.name }} · <code>{{ visitTimelineObject.unique_url }}</code>
							</small>
						</div>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<ShortlinkVisitTimeline v-if="visitTimelineObject" :key="visitTimelineObject.id" type="delivery_object" :id="visitTimelineObject.id" />
					</div>
				</div>
			</div>
		</div>

		<PostcardPatternEditor
			ref="patternEditor"
			:front-image="campaign.postcard_front ? storageUrl + campaign.postcard_front : ''"
			:back-image="campaign.postcard_back ? storageUrl + campaign.postcard_back : ''"
			@save="onPatternsSaved"
		/>

		<!-- Modal for creating carnivore -->
		<div class="modal fade" id="carnivoreModal" tabindex="-1" aria-labelledby="carnivoreModalLabel" aria-hidden="true">
			<div class="modal-dialog">
				<div class="modal-content">
					<form @submit.prevent="createCarnivore">
						<div class="modal-header">
							<h5 class="modal-title">Create Carnivore</h5>
							<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						</div>
						<div class="modal-body">
							<div class="form-group mb-1">
								<label for="">Carnivore Name</label>
								<div class="input-group mb-3">
									<input type="text" class="form-control" placeholder="Carnivore name" v-model="newCarnivore.name" required />
								</div>
							</div>
							<div class="form-group mb-1">
								<label for="">Description</label>
								<div class="input-group mb-3">
									<textarea class="form-control" placeholder="Carnivore description" v-model="newCarnivore.description" />
								</div>
							</div>
							<div class="form-group mb-1">
								<label for="">Priority</label>
								<select class="form-select" v-model="newCarnivore.priority">
									<option value="normal">Normal</option>
									<option value="medium">Medium</option>
									<option value="high">High</option>
								</select>
							</div>

							<div class="row mt-2">
								<div class="col-md-12">
									Chose fields for campaigns
								</div>
								<div class="col-md-6" v-for="field in newCarnivore.fields" :key="field.name">
									<div class="form-check">
										<input class="form-check-input" type="checkbox" v-model="field.active" :id="field.name" />
										<label class="form-check-label" :for="field.name">
											{{ field.label }}
										</label>
									</div>
								</div>
							</div>

							<div class="mt-2">
								<span class="badge badge-primary bg-success w-100 py-3 mt-3"
									><h4 class="m-0 p-0">{{ pagination.total }} municipalities</h4></span
								>
							</div>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
							<button type="submit" class="btn btn-primary">
								Create
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>

		<!-- Modal for sending live campaign -->
		<div class="modal fade" id="sendLiveModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-labelledby="createLabel" aria-hidden="true">
			<div class="modal-dialog modal-dialog-centered">
				<div class="modal-content rounded">
					<div class="modal-header">
						<h5 class="modal-title" id="exampleModalLabel">{{ campaign.name }}</h5>

						<button type="button" class="btn-close" @click="closeLiveModal()" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div v-if="!loadingLivePreview">
							<div class="row">
								<div class="col-12">
									<img v-if="liveResponseData.data.thumbnail !== ''" :src="liveResponseData.data.thumbnail" alt="" class="w-100" />
								</div>
								<div class="col-12">
									<div class="alert alert-info bg-info-lighter text-info">
										💡 You can preview your campaign PDF by clicking on the "Preview Campaign" button. This preview is sent as a test in a testing environment,
										and it represents a 100% accurate depiction of the real delivery.
									</div>
									<a target="_blank" :href="liveResponseData.data.url" class="btn btn-primary w-100"> Preview campaign</a>
								</div>
							</div>
						</div>
						<div v-if="loadingLivePreview" class="div">
							<div class="d-flex justify-content-center">
								<div class="spinner-border me-3" role="status">
									<span class="visually-hidden">Loading...</span>
								</div>
								Preparing campaign ...
							</div>
						</div>
						<div class="row mt-3">
							<div class="col-md-12">
								<div class="alert alert-info bg-success-lighter text-info text-center">
									💸 Estimated campaign cost, based on settings and the number of Delivery Objects, is <br />
									<span class="fs-1 fw-bold text-success">~${{ costsEstimated }} </span>
									<br />
									💡 The estimated cost is accurate unless there are errors from LOB. If errors occur, we will subtract them and display a more accurate
									estimation in the campaign sent view.
								</div>
							</div>
							<div class="col-md-12">
								<div class="alert alert-info bg-info-lighter text-info text-center">
									💡 After you press the "send" button, the campaign will be scheduled for sending, and the sending process will commence after one hour.
								</div>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" @click="closeLiveModal()" class="btn btn-secondary" data-bs-dismiss="modal">
							Close
						</button>

						<button type="button" @click="sendCampaign()" :disabled="loadingLivePreview" class="btn btn-success">
							<font-awesome-icon :icon="['fas', sendCampaignLoading ? 'spinner' : 'shipping-fast']" :class="[sendCampaignLoading ? 'fa-spin' : '']" />
							Send
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal Adding new Delivery Object -->
		<div
			class="modal fade"
			id="addDeliveryObjectModal"
			tabindex="-1"
			aria-labelledby="addDeliveryObjectModalLabel"
			aria-hidden="true"
			data-bs-backdrop="static"
			data-bs-keyboard="false"
		>
			<div class="modal-dialog modal-dialog-centered">
				<div class="modal-content rounded">
					<div class="modal-header">
						<div class="d-flex justify-content-start align-items-center">
							<h5 class="modal-title me-2">Add Delivery Object</h5>
						</div>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body mb-0">
						<div class="alert alert-warning bg-warning-lighter text-dark mb-3">
							💡 The delivery object can be a completely independent entity that does not exist in the database but must represent a valid address located in the
							United States.
						</div>
						<div class="alert alert-info bg-info-light mb-3">
							👋 Adding a delivery object to the campaign will only incorporate that object within the campaign itself. It will not modify any other databases or data
							stored in the system, even if the same object already exists elsewhere in the system.
						</div>
						<div class="mb-3">
							Delivery Object Type:
							<span class="badge bg-info-light">{{ dObjectNew.object_type }}</span>
						</div>

						<div class="form-group mb-2">
							<label for="">Object Type</label>
							<select v-model="dObjectNew.object_type" class="form-select">
								<option value="custom">Custom</option>
								<option value="municipality">Municipality</option>
								<option value="client">Client</option>
							</select>
						</div>

						<div v-if="dObjectNew.object_type == 'municipality'" class="mb-2">
							<MunicipalityAdvancedSearch v-on:setMunicipality="setMunicipality" />
						</div>
						<div v-if="dObjectNew.object_type == 'client'" class="mb-2">
							<ClientsSearch :selected="null" v-on:setClient="setClient" />
						</div>
						<div class="form-group mb-2">
							<label for="">Name</label>
							<input type="text" v-model="dObjectNew.name" class="form-control" />
						</div>
						<div class="form-group mb-2">
							<label for="">State</label>
							<select v-model="dObjectNew.state" class="form-select" name="" id="">
								<option :key="usState.abbreviation" v-for="usState in usStates" :value="usState.abbreviation">{{ usState.name }}</option>
							</select>
						</div>
						<div class="form-group mb-2">
							<label for="">City</label>
							<input type="text" v-model="dObjectNew.city" class="form-control" />
						</div>
						<div class="form-group mb-2">
							<label for="">Address</label>
							<input type="text" v-model="dObjectNew.address" class="form-control" />
						</div>
						<div class="form-group mb-2">
							<label for="">Zip</label>
							<input type="text" v-model="dObjectNew.zip" class="form-control" />
						</div>
					</div>
					<div class="modal-footer p-0 px-1 py-1">
						<button type="button" class="btn btn-default" data-bs-dismiss="modal" aria-label="Close">
							Cancel
						</button>
						<button type="button" class="btn btn-primary float-end" @click="storeDeliveryObject()">
							<font-awesome-icon :icon="['fas', storingDeliveryObjectFlag ? 'spinner' : 'save']" :class="[storingDeliveryObjectFlag ? 'fa-spin' : '']" /> Add Object
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal for editing Delivery Object -->
		<div
			class="modal fade"
			id="editDeliveryObjectModal"
			tabindex="-1"
			aria-labelledby="editDeliveryObjectModalLabel"
			aria-hidden="true"
			data-bs-backdrop="static"
			data-bs-keyboard="false"
		>
			<div class="modal-dialog modal-dialog-centered">
				<div class="modal-content rounded">
					<div class="modal-header">
						<div class="d-flex justify-content-start align-items-center">
							<h5 class="modal-title me-2">Edit Delivery Object</h5>
						</div>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body mb-0">
						<div>
							<div class="mb-3">
								Delivery Object Type:
								<span class="badge bg-info-light">{{ dObjectToEdit.object_type }}</span>
							</div>
							<div class="form-group mb-2">
								<label for="">Name</label>
								<input type="text" v-model="dObjectToEdit.name" class="form-control" />
							</div>
							<div class="form-group mb-2">
								<label for="">State</label>
								<select v-model="dObjectToEdit.state" class="form-select" name="" id="">
									<option :key="usState.abbreviation" v-for="usState in usStates" :value="usState.abbreviation">{{ usState.name }}</option>
								</select>
							</div>
							<div class="form-group mb-2">
								<label for="">City</label>
								<input type="text" v-model="dObjectToEdit.city" class="form-control" />
							</div>
							<div class="form-group mb-2">
								<label for="">Address</label>
								<input type="text" v-model="dObjectToEdit.address" class="form-control" />
							</div>
							<div class="form-group mb-2">
								<label for="">Zip</label>
								<input type="text" v-model="dObjectToEdit.zip" class="form-control" />
							</div>
							<div class="alert alert-primary bg-primary-light my-3">
								👋 If you want to update the municipality record with these changes, please check the checkbox below. Otherwise, it will simply update the delivery
								object only for this campaign.
								<div class="form-check mt-2">
									<input
										:disabled="dObjectToEdit.object_type == 'client'"
										v-model="dObjectToEdit.updateType"
										class="form-check-input"
										type="checkbox"
										value=""
										id="flexCheckDefault"
									/>
									<label class="form-check-label" for="flexCheckDefault"> Update {{ dObjectToEdit.object_type }} also </label>
								</div>
							</div>
						</div>
					</div>
					<div class="modal-footer p-0 px-1 py-1">
						<button type="button" class="btn btn-default" data-bs-dismiss="modal" aria-label="Close">
							Cancel
						</button>
						<button type="button" class="btn btn-primary float-end" @click="updateDeliveryObject()">
							<font-awesome-icon :icon="['fas', updatingDeliveryObjectFlag ? 'spinner' : 'save']" :class="[updatingDeliveryObjectFlag ? 'fa-spin' : '']" /> Update
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal for generating letter pattern -->
		<div
			class="modal fade "
			id="campaignLetterPatternHtmlModal"
			tabindex="-1"
			aria-labelledby="campaignLetterPatternHtmlModalLabel"
			aria-hidden="true"
			data-bs-backdrop="static"
			data-bs-keyboard="false"
			data-bs-focus="false"
		>
			<div class="modal-dialog modal-xl">
				<div class="modal-content">
					<div class="modal-header">
						<div class="d-flex justify-content-start align-items-center">
							<h5 class="modal-title me-2"><font-awesome-icon :icon="['fab', 'html5']" /> Letter Pattern HTML</h5>
						</div>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body mb-0">
						<div v-if="!displayPreviewPdf" class="row">
							<div class="col-md-9">
								<Editor v-model="campaign.letter_pattern_html" :init="vueLetterPatternToolbar" />
							</div>
							<div class="col-md-3">
								Awailable variables
								<div class="variables-list-wrapper">
									<input
										v-for="(variable, i) in patternVariables"
										:key="i + '-patern-id'"
										type="text"
										class="form-control form-control-sm form-control-plaintext"
										:value="variable"
									/>
								</div>
							</div>
						</div>
						<div v-if="displayPreviewPdf" class="pdf-preview-window">
							<div>
								<button class="btn btn-primary mb-2" @click="displayPreviewPdf = false">
									Close Preview
								</button>
							</div>
							<object :data="customLetterPdfUrl" type="application/pdf" width="100%" height="1250px">
								<p>
									Unable to display PDF file.
									<a target="_blank" :href="customLetterPdfUrl">Download</a>
									instead.
								</p>
							</object>
						</div>
					</div>
					<div class="modal-footer p-0 px-1 py-1">
						<button type="button" class="btn btn-primary" @click="previewPdf()"><font-awesome-icon :icon="['fas', 'file-pdf']" class="me-2" /> Preview PDF</button>
						<button type="button" class="btn btn-default" data-bs-dismiss="modal" aria-label="Close">
							Cancel
						</button>
						<button type="button" class="btn btn-primary float-end" @click="updateCampaign()">
							Save
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader'
import UtmCampaignInput from '@/components/common/UtmCampaignInput'
import UtmContentInput from '@/components/common/UtmContentInput'
import { normalizeUtmCampaign } from '@/utils/utm'
import { mapState } from 'vuex'
import restApi from '../../api/index.js'
import { handleErrors } from '../../actions/helpers'
import { Modal } from 'bootstrap'
import LPDataTablePagination from '@/components/lp-data-table/LPDataTablePagination'
import Editor from '@tinymce/tinymce-vue'
import MunicipalityAdvancedSearch from '../../components/municipality/MunicipalityAdvancedSearch.vue'
import ClientsSearch from '../../components/clients/ClientsSearch.vue'
import PostcardPatternEditor from '@/components/campaign/PostcardPatternEditor.vue'
import ShortlinkVisitTimeline from '@/components/campaign/ShortlinkVisitTimeline.vue'
import ShortlinkDomainSelect from '@/components/campaign/ShortlinkDomainSelect.vue'
import { buildPatterns, parsePatterns, patternsUseUniqueUrl } from '@/utils/postcardPatterns'
import { formatUtc } from '@/utils/time'

export default {
	name: 'CampaignEdit',
	components: { PageHeader, LPDataTablePagination, Editor, MunicipalityAdvancedSearch, ClientsSearch, UtmCampaignInput, UtmContentInput, PostcardPatternEditor, ShortlinkVisitTimeline, ShortlinkDomainSelect },
	data() {
		return {
			// Postcard template patterns per side, saved as campaign.postcard_pattern_type
			postcardPatterns: { front: [], back: [] },
			templatePreview: { loading: false, error: null, images: [], files: [], sample: {} },
			// Recipient whose short-link visit timeline is open
			visitTimelineObject: null,
			applyingDomain: false,
			utmEdit: { open: false, saving: false, value: '', content: '' },
			usStates: [
				{ abbreviation: 'AL', name: 'Alabama' },
				{ abbreviation: 'AK', name: 'Alaska' },
				{ abbreviation: 'AZ', name: 'Arizona' },
				{ abbreviation: 'AR', name: 'Arkansas' },
				{ abbreviation: 'CA', name: 'California' },
				{ abbreviation: 'CO', name: 'Colorado' },
				{ abbreviation: 'CT', name: 'Connecticut' },
				{ abbreviation: 'DE', name: 'Delaware' },
				{ abbreviation: 'FL', name: 'Florida' },
				{ abbreviation: 'GA', name: 'Georgia' },
				{ abbreviation: 'HI', name: 'Hawaii' },
				{ abbreviation: 'ID', name: 'Idaho' },
				{ abbreviation: 'IL', name: 'Illinois' },
				{ abbreviation: 'IN', name: 'Indiana' },
				{ abbreviation: 'IA', name: 'Iowa' },
				{ abbreviation: 'KS', name: 'Kansas' },
				{ abbreviation: 'KY', name: 'Kentucky' },
				{ abbreviation: 'LA', name: 'Louisiana' },
				{ abbreviation: 'ME', name: 'Maine' },
				{ abbreviation: 'MD', name: 'Maryland' },
				{ abbreviation: 'MA', name: 'Massachusetts' },
				{ abbreviation: 'MI', name: 'Michigan' },
				{ abbreviation: 'MN', name: 'Minnesota' },
				{ abbreviation: 'MS', name: 'Mississippi' },
				{ abbreviation: 'MO', name: 'Missouri' },
				{ abbreviation: 'MT', name: 'Montana' },
				{ abbreviation: 'NE', name: 'Nebraska' },
				{ abbreviation: 'NV', name: 'Nevada' },
				{ abbreviation: 'NH', name: 'New Hampshire' },
				{ abbreviation: 'NJ', name: 'New Jersey' },
				{ abbreviation: 'NM', name: 'New Mexico' },
				{ abbreviation: 'NY', name: 'New York' },
				{ abbreviation: 'NC', name: 'North Carolina' },
				{ abbreviation: 'ND', name: 'North Dakota' },
				{ abbreviation: 'OH', name: 'Ohio' },
				{ abbreviation: 'OK', name: 'Oklahoma' },
				{ abbreviation: 'OR', name: 'Oregon' },
				{ abbreviation: 'PA', name: 'Pennsylvania' },
				{ abbreviation: 'RI', name: 'Rhode Island' },
				{ abbreviation: 'SC', name: 'South Carolina' },
				{ abbreviation: 'SD', name: 'South Dakota' },
				{ abbreviation: 'TN', name: 'Tennessee' },
				{ abbreviation: 'TX', name: 'Texas' },
				{ abbreviation: 'UT', name: 'Utah' },
				{ abbreviation: 'VT', name: 'Vermont' },
				{ abbreviation: 'VA', name: 'Virginia' },
				{ abbreviation: 'WA', name: 'Washington' },
				{ abbreviation: 'WV', name: 'West Virginia' },
				{ abbreviation: 'WI', name: 'Wisconsin' },
				{ abbreviation: 'WY', name: 'Wyoming' },
			],
			loadingLivePreview: false,
			loading: false,
			deliveryObjects: [],
			campaign: {
				name: 'Campaign',
				municipalities: [],
			},
			localParams: {},
			printingFilters: [],
			municipalities: [],
			clients: [],
			deliveries: [],
			pagination: {},
			page: this.$route.query.page || 1,
			uploadLoading: false,
			senders: [],
			missing: {
				address: 0,
				city: 0,
				zip: 0,
			},
			singleCancelLoading: false,
			sendCampaignLoading: false,
			campaignCancelLoading: false,
			$sendLiveModal: null,
			liveResponseData: {
				error: '',
				data: {
					id: '',
					url: '',
					thumbnail: '',
				},
			},
			sentDeliveries: 0,
			newDataEntrySegment: {},
			$carnivoreModal: null,
			newCarnivore: {
				name: '',
				type: 'default',
				campaign_id: null,
				priority: 'normal',
				description: '',
				workers: null,
				fields: [
					{
						name: 'contacts',
						label: 'Contacts',
						active: true,
					},
					{
						name: 'email',
						label: 'Email',
						active: true,
					},
					{
						name: 'phone',
						label: 'Phone',
						active: true,
					},
					{
						name: 'county',
						label: 'County',
						active: true,
					},
					{
						name: 'city',
						label: 'City',
						active: true,
					},
					{
						name: 'zip',
						label: 'Zip',
						active: true,
					},
					{
						name: 'address_street',
						label: 'Address street / PO Box',
						active: true,
					},
					{
						name: 'population',
						label: 'Population',
						active: true,
					},
					{
						name: 'issues',
						label: 'Issues',
						active: false,
					},
					{
						name: 'boat_launch',
						label: 'Boat launch',
						active: false,
					},
					{
						name: 'park_rental',
						label: 'Park rental',
						active: false,
					},
					{
						name: 'online_payments',
						label: 'Online payments',
						active: false,
					},
					{
						name: 'fillable_forms',
						label: 'Fillable forms',
						active: false,
					},
					{
						name: 'chat_box',
						label: 'Chat box',
						active: false,
					},
					{
						name: 'alert_notifications',
						label: 'Alert notifications',
						active: false,
					},
				],
				status: 'new',
				data: null,
				filters: '',
				municipalities_count: '',
				municipalities: [],
			},

			$campaignLetterPatternHtmlModal: null,
			vueLetterPatternToolbar: {
				height: '100%',
				menubar: false,
				plugins: 'link code image lists',
				toolbar1:
					'fontselect fontsizeselect formatselect lineheight | bold italic underline strikethrough | bullist numlist | alignleft aligncenter alignright alignjustify | link image removeformat code',
			},
			patternVariables: [],
			customLetterPdfUrl: '',
			displayPreviewPdf: false,
			$addDeliveryObjectModal: null,
			$editDeliveryObjectModal: null,
			dObjectToEdit: {
				object_type: '',
				name: '',
				state: '',
				city: '',
				address: '',
				zip: '',
			},
			dObjectNew: {
				object_type: 'custom',
				name: '',
				state: '',
				city: '',
				address: '',
				zip: '',
			},
			updatingDeliveryObjectFlag: false,
			storingDeliveryObjectFlag: false,
			deliveryObjectSearch: '',
			enableEditMainInfo: false,
			deliveryObjectsCount: 0,
			deliveryErrorsCount: 0,
			deliveryCanceledCount: 0,
			costsEstimated: 156,
			errorsCountLoading: false,
			objectsCountLoading: false,
			canceledCountLoading: false,
			sentIndicator: 0,
			loadingCampaign: false,
			intervalId: null,
			campaignInterval: null,
			statusFilter: '',
		}
	},
	computed: {
		...mapState(['authUser', 'storageUrl']),
		hasTemplatePatterns() {
			return this.postcardPatterns.front.length + this.postcardPatterns.back.length > 0
		},
		templateUsesUniqueUrl() {
			return patternsUseUniqueUrl([...this.postcardPatterns.front, ...this.postcardPatterns.back])
		},
		// Does what gets printed for this campaign contain the recipient's short link / QR code?
		campaignPrintsShortLink() {
			if (this.campaign.type === 'letter') {
				return (
					this.campaign.letter_type === 'single-item-pattern' &&
					this.campaign.letter_pattern === 'custom' &&
					String(this.campaign.letter_pattern_html || '').includes('$unique_url')
				)
			}
			return this.templateUsesUniqueUrl
		},
		totalSentDeliveryObjects() {
			return this.deliveryObjectsCount - this.deliveryErrorsCount - this.deliveryCanceledCount
		},
	},
	mounted: function() {
		this.$sendLiveModal = new Modal('#sendLiveModal')
		this.$carnivoreModal = new Modal('#carnivoreModal')
		this.$addDeliveryObjectModal = new Modal('#addDeliveryObjectModal')
		this.$editDeliveryObjectModal = new Modal('#editDeliveryObjectModal')
		this.$campaignLetterPatternHtmlModal = new Modal('#campaignLetterPatternHtmlModal')
		this.$postcardPreviewModal = new Modal('#postcardPreviewModal')
		this.$visitTimelineModal = new Modal('#visitTimelineModal')
		this.intervalId = setInterval(() => {
			if (this.campaign.status == 'abborted') {
				this.getCampaign()
			}
		}, 10000)
		this.campaignInterval = setInterval(() => {
			if (this.campaign.status == 'canceled') {
				this.getDeliveryObjects()
			}
		}, 20000)
	},
	beforeDestroy() {
		this.cleanupTemplatePreview()
		// Stop the interval when the component is about to be destroyed
		clearInterval(this.intervalId)
		clearInterval(this.campaignInterval)
	},
	created() {
		this.getSettingsSenders()
		this.getCampaign()
	},
	methods: {
		/**
		 * Get Campaign
		 * Function get selected campaign data
		 */
		getCampaign(sendingCheck = false) {
			this.loadingCampaign = true
			restApi
				.get(`/campaigns/${this.$route.params.id}`)
				.then(({ data }) => {
					if (data.status != 'abborted') {
						clearInterval(this.intervalId)
					}
					// If campaign status is "scheduled"
					if (data.status == 'scheduled') {
						this.$router.push(`/campaigns/scheduled/${this.$route.params.id}`)
					}
					// If campaign status is "sending"
					if (data.status == 'sending') {
						this.$router.push(`/campaigns/sending/${this.$route.params.id}`)
					}
					this.printingFilters = []
					data.filters = JSON.parse(data.filters)
					this.campaign = data
					this.postcardPatterns = parsePatterns(data.postcard_pattern_type)
					this.localParams = data.filters
					this.newCarnivore.name = this.campaign.name
					this.newCarnivore.campaign_id = this.campaign.id

					if (this.campaign.status == 'preparing') {
						// Adapt send_at for input
						if (this.campaign.send_at) {
							this.campaign.send_at = this.campaign.send_at.replace(' ', 'T')
						}
						this.getDeliveryObjects()
					} else {
						if (!sendingCheck) {
							this.getDeliveryObjects()
							this.getDeliveryStats()
						}
					}

					for (const property in this.localParams) {
						if (property !== 'page' && property !== 'orderBy' && property !== 'direction' && property !== 'perPage') {
							let value = this.localParams[property]
							if (value) {
								value === 'Yes'
							}
							if (!value) {
								value === 'No'
							}
							this.printingFilters.push(`${property}: ${value}`)
						}
					}
					this.loadingCampaign = false
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, 'error'),
						timer: 6000,
					})
				})
		},

		/**
		 * Get Delivery Objects
		 * Function getting all Delivery Objects for current campaign
		 */
		getDeliveryObjects() {
			this.loading = true
			this.params = {
				page: this.page,
				search: this.deliveryObjectSearch,
				status: this.statusFilter,
			}
			restApi
				.get(`/campaigns/${this.$route.params.id}/delivery-objects`, { params: this.params })
				.then(({ data }) => {
					this.deliveryObjects = data.data
					this.pagination = data
					delete this.pagination.data
					this.loading = false
					//this.$router.push({ path: this.$router.currentPath, query: this.params })
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		/**
		 * Get Delivery Stats
		 * Function get all delivery states
		 */
		getDeliveryStats() {
			this.getDeliveryObjectsCount()
			this.getDeliveryErrorsCount()
			this.getDeliveryCanceledCount()
		},

		/**
		 * Get Delivery Objects Count
		 * Function getting count of delivery objects
		 */
		getDeliveryObjectsCount() {
			this.objectsCountLoading = true
			restApi
				.get(`/campaigns/${this.$route.params.id}/delivery-objects-count`)
				.then(({ data }) => {
					this.deliveryObjectsCount = data
					this.objectsCountLoading = false
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		/**
		 * Get Delivery Errors Count
		 * Function getting count of delivery errors
		 */
		getDeliveryErrorsCount() {
			this.errorsCountLoading = true
			restApi
				.get(`/campaigns/${this.$route.params.id}/delivery-errors-count`)
				.then(({ data }) => {
					this.deliveryErrorsCount = data
					this.errorsCountLoading = false
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		/**
		 * Get Delivery Canceled Count
		 * Function getting count of delivery canceled
		 */
		getDeliveryCanceledCount() {
			this.canceledCountLoading = true
			restApi
				.get(`/campaigns/${this.$route.params.id}/delivery-canceled-count`)
				.then(({ data }) => {
					this.deliveryCanceledCount = data
					this.canceledCountLoading = false
					this.estimateCampaignCosts(this.totalSentDeliveryObjects)
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		/**
		 * Edit Delivery Object
		 * Function prepare Delivery Object for editing
		 */
		editDeliveryObject(dObject) {
			this.dObjectToEdit = dObject
			this.$editDeliveryObjectModal.show()
		},

		/**
		 * Update Delivery Object
		 * Function updates Delivery Object
		 */
		updateDeliveryObject() {
			this.updatingDeliveryObjectFlag = true
			restApi
				.put(`/delivery-objects/${this.dObjectToEdit.id}`, this.dObjectToEdit)
				.then(() => {
					this.$editDeliveryObjectModal.hide()
					this.updatingDeliveryObjectFlag = false
					this.$swal.fire({
						icon: 'success',
						title: 'Delivery Object Updated!',
						timer: 3000,
					})
				})
				.catch(error => {
					this.updatingDeliveryObjectFlag = false
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		/**
		 * Add Delovery Object
		 * Function prepares new Delivery Object data and open modal
		 */
		addDeliveryObject() {
			this.$addDeliveryObjectModal.show()
		},

		/**
		 * Store Delivery Object
		 * Function adds Delivery Object to current campaign
		 */
		storeDeliveryObject() {
			this.storingDeliveryObjectFlag = true
			this.dObjectNew.object_type = this.campaign.objects_type
			this.dObjectNew.campaign_id = this.campaign.id
			restApi
				.post(`/delivery-objects`, this.dObjectNew)
				.then(() => {
					this.getCampaign()
					this.getDeliveryObjects()
					this.$addDeliveryObjectModal.hide()
					this.storingDeliveryObjectFlag = false
					this.$swal.fire({
						icon: 'success',
						title: 'Delivery Object Added!',
						timer: 3000,
					})
					this.dObjectNew.name = ''
					this.dObjectNew.state = ''
					this.dObjectNew.city = ''
					this.dObjectNew.address = ''
					this.dObjectNew.zip = ''
					this.dObjectNew.municipality_id = null
				})
				.catch(error => {
					this.storingDeliveryObjectFlag = false
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		/**
		 * Delete Delivery Object
		 * Function remove delivery object from campaign (not affectiong any other databse)
		 */
		deleteDeliveryObject(id) {
			this.$swal
				.fire({
					title: 'Are you sure want to delete this Delivery Object?',
					text: `Deleting the Delivery Object will only impact the corresponding entity within this campaign and will not affect any other data in the database.`,
					icon: 'warning',
					position: 'center',
					showCancelButton: true,
					showConfirmButton: true,
					confirmButtonColor: '#3085d6',
					cancelButtonColor: '#d33',
					confirmButtonText: 'Yes, delete it!',
					toast: false,
					timer: 0,
				})
				.then(result => {
					if (result.isConfirmed) {
						restApi
							.delete(`/delivery-objects/` + id)
							.then(() => {
								this.getCampaign()
								this.getDeliveryObjects()
							})
							.catch(error => {
								this.$swal.fire({
									icon: 'error',
									title: handleErrors(error, ''),
									timer: 6000,
								})
							})
					}
				})
		},

		/**
		 * Update Campaign
		 * Function update campaign data
		 */
		openUtmEdit() {
			this.utmEdit = { open: true, saving: false, value: this.campaign.utm_campaign || '', content: this.campaign.utm_content || '' }
		},

		// Sent/canceled campaigns: only the UTM values are saved, nothing else on the campaign changes.
		// utm_content applies to short-link visits from now on.
		saveUtmCampaign() {
			this.utmEdit.saving = true
			restApi
				.put(`/campaigns/${this.campaign.id}`, {
					name: this.campaign.name,
					utm_campaign: normalizeUtmCampaign(this.utmEdit.value),
					utm_content: normalizeUtmCampaign(this.utmEdit.content),
				})
				.then(({ data }) => {
					this.campaign.utm_campaign = data.utm_campaign
					this.campaign.utm_content = data.utm_content
					this.utmEdit.open = false
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, 'error'),
						timer: 6000,
					})
				})
				.finally(() => {
					this.utmEdit.saving = false
				})
		},

		openPatternEditor(side) {
			this.$refs.patternEditor.open(side, this.postcardPatterns[side])
		},
		onPatternsSaved({ side, patterns }) {
			this.postcardPatterns = { ...this.postcardPatterns, [side]: patterns }
			// A link/QR pattern only works with tracking on, so switch it on right away.
			if (this.templateUsesUniqueUrl && !this.campaign.unique_url_enabled) {
				this.campaign.unique_url_enabled = true
			}
			this.updateCampaign().then(saved => {
				if (saved && this.campaign.unique_url_enabled && !this.campaign.unique_url_domain) {
					this.$swal.fire({
						icon: 'info',
						title: 'Add a landing domain',
						text: 'Unique URL tracking is on. Set the landing domain in "Unique URL tracking" before sending the campaign.',
					})
				}
			})
		},
		// Readable message from an API error (validation errors, { message } or a plain failure).
		apiErrorMessage(error) {
			const data = (error.response && error.response.data) || {}
			if (data.message) return data.message
			if (typeof data.error === 'string') return data.error
			if (data.error && typeof data.error === 'object') {
				const first = Object.values(data.error)[0]
				if (first) return Array.isArray(first) ? first[0] : first
			}
			return error.message || 'Something went wrong.'
		},
		async previewTemplate() {
			this.cleanupTemplatePreview()
			this.templatePreview = { loading: true, error: null, images: [], files: [], sample: {} }
			this.$postcardPreviewModal.show()
			try {
				// Current (possibly unsaved) template and tracking settings
				const { data } = await restApi.post(`/campaigns/${this.campaign.id}/template-preview`, {
					patterns: buildPatterns(this.postcardPatterns.front, this.postcardPatterns.back),
					unique_url_domain: this.campaign.unique_url_domain || '',
					shortlink_domain: this.campaign.shortlink_domain || null,
					utm_campaign: this.campaign.utm_campaign || '',
				})
				const files = data.data.files || []
				this.templatePreview = {
					loading: false,
					error: null,
					files,
					sample: data.data.sample || {},
					images: files.map(file => `${this.storageUrl}${file}?preview=${Date.now()}`),
				}
			} catch (error) {
				this.templatePreview = { loading: false, error: this.apiErrorMessage(error), images: [], files: [], sample: {} }
			}
		},
		cleanupTemplatePreview() {
			if (this.templatePreview.files.length) {
				restApi.post('/auto-campaign-lob/delete-template-test', { files: this.templatePreview.files }).catch(() => {})
			}
		},
		async applyDomainToUnsent() {
			this.applyingDomain = true
			try {
				const saved = await this.updateCampaign()
				if (!saved) return
				const { data } = await restApi.post(`/campaigns/${this.campaign.id}/shortlinks/apply-domain`)
				this.$swal.fire({ icon: 'success', title: `${data.updated} recipient link${data.updated === 1 ? '' : 's'} moved to the selected domain`, timer: 3000, showConfirmButton: false })
			} catch (error) {
				this.$swal.fire({ icon: 'error', title: this.apiErrorMessage(error), timer: 6000 })
			} finally {
				this.applyingDomain = false
			}
		},
		openVisitTimeline(dObject) {
			this.visitTimelineObject = dObject
			this.$visitTimelineModal.show()
		},
		formatVisit(dObject) {
			const channel = dObject.unique_url_visit_info && dObject.unique_url_visit_info.channel
			const label = channel === 'qr' ? ' · QR scan' : channel === 'typed' ? ' · Typed link' : ''
			const visits = dObject.unique_url_visits > 1 ? ` · ${dObject.unique_url_visits} visits` : ''
			return formatUtc(dObject.unique_url_visited_at, 'MMM D, YYYY h:mm A') + label + visits
		},
		updateCampaign() {
			const patterns = buildPatterns(this.postcardPatterns.front, this.postcardPatterns.back)
			this.campaign.postcard_pattern_type = patterns.length ? JSON.stringify(patterns) : null
			this.campaign.estimated_costs = this.costsEstimated
			return restApi
				.put(`/campaigns/${this.$route.params.id}`, this.campaign)
				.then(({ data }) => {
					delete data.changes.updated_at
					delete data.changes.filters
					this.$campaignLetterPatternHtmlModal.hide()
					this.$swal.fire({
						icon: 'success',
						title: 'Campaign successfully updated!',
						timer: 3000,
					})
					return true
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: this.apiErrorMessage(error),
						timer: 6000,
					})
					return false
				})
			},

		// Get items from page that returned from LPDataTablePagination
		getItems(page) {
			this.page = page
			this.getDeliveryObjects()
		},

		// Upload
		campaignUploads($event, name) {
			this.uploadLoading = true
			var form = new FormData()
			form.append(name, $event.target.files[0])
			form.append('id', this.campaign.id)
			form.append('type', this.campaign.type)
			form.append('name', name)

			const config = {
				headers: { 'content-type': 'multipart/form-data' },
			}

			restApi
				.post('/campaigns/upload', form, config)
				.then(({ data }) => {
					this.campaign[name] = data
					this.uploadLoading = false
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, 'campaignUpload'),
						timer: 6000,
					})
					this.uploadLoading = false
				})
		},

		// Remove upload
		removeUpload(name) {
			restApi
				.post('/campaigns/upload-destroy', { name: name, id: this.campaign.id })
				.then(() => {
					this.campaign[name] = ''
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, 'campaignUpload'),
						timer: 6000,
					})
				})
		},

		/**
		 * Send Campaign
		 * Ensuring that all changes are saved first.Then proceeding to schedule the campaign for sending (Note: it won't be sent immediately, but rather scheduled for one hour from now).
		 */
		sendCampaign() {
			this.sendCampaignLoading = true
			restApi
				.put(`/campaigns/${this.$route.params.id}`, this.campaign)
				.then(() => {
					restApi
						.post(`/campaigns/send/${this.campaign.id}`)
						.then(({ data }) => {
							this.campaign = data
							this.$sendLiveModal.hide()
							this.closeLiveModal()
							this.sendCampaignLoading = false
							this.$router.push(`/campaigns/scheduled/${this.$route.params.id}`)
						})
						.catch(error => {
							this.$swal.fire({
								icon: 'error',
								title: this.apiErrorMessage(error),
								timer: 6000,
							})
							this.sendCampaignLoading = false
						})
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Get available senders from LOBSettings
		getSettingsSenders() {
			restApi
				.get('/settings', { params: { name: 'LobSettings', type: 'settings' } })
				.then(({ data }) => {
					if (data) {
						let settings = JSON.parse(data.data)
						this.senders = settings.senders
					}
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Delete sent campaign
		deleteSentCampaign() {
			this.$swal
				.fire({
					title: 'Are you sure?',
					text: `Not that It will also remove all deliveries connected with this campaign! You won't be able to revert this! `,
					icon: 'warning',
					position: 'center',
					showCancelButton: true,
					showConfirmButton: true,
					confirmButtonColor: '#3085d6',
					cancelButtonColor: '#d33',
					confirmButtonText: 'Yes, delete it!',
					toast: false,
					timer: 0,
				})
				.then(result => {
					if (result.isConfirmed) {
						restApi
							.delete(`/campaigns/` + this.campaign.id)
							.then(() => {
								this.$router.push(`/campaigns`)
								this.$swal.fire({
									icon: 'success',
									title: `Campaign ${this.campaign.name} successfully deleted!`,
								})
							})
							.catch(error => {
								this.$swal.fire({
									icon: 'error',
									title: handleErrors(error, ''),
									timer: 6000,
								})
							})
					}
				})
		},

		deletePreparedCampaign() {
			this.$swal
				.fire({
					title: 'Are you sure?',
					text: `You won't be able to revert this! `,
					icon: 'warning',
					position: 'center',
					showCancelButton: true,
					showConfirmButton: true,
					confirmButtonColor: '#3085d6',
					cancelButtonColor: '#d33',
					confirmButtonText: 'Yes, delete it!',
					toast: false,
					timer: 0,
				})
				.then(result => {
					if (result.isConfirmed) {
						restApi
							.delete(`/campaigns/` + this.campaign.id)
							.then(() => {
								this.$router.push(`/campaigns`)
								this.$swal.fire({
									icon: 'success',
									title: `Campaign ${this.campaign.name} successfully deleted!`,
								})
							})
							.catch(error => {
								this.$swal.fire({
									icon: 'error',
									title: handleErrors(error, ''),
									timer: 6000,
								})
							})
					}
				})
		},

		// Check is cancel available
		checkCancelable(delivery) {
			var deliveryTime = new Date(delivery.send_at).getTime()
			var nowTime = new Date(new Date().toUTCString().slice(0, -3)).getTime()
			return deliveryTime > nowTime
		},

		// Cancel delivery
		cancelDelivery(delivery) {
			this.$swal
				.fire({
					title: 'Are you sure want to cancel this delivery?',
					text: `You won't be able to revert this! `,
					icon: 'warning',
					position: 'center',
					showCancelButton: true,
					showConfirmButton: true,
					confirmButtonColor: '#3085d6',
					cancelButtonColor: '#d33',
					confirmButtonText: 'Yes, cancel it!',
					toast: false,
					timer: 0,
				})
				.then(result => {
					if (result.isConfirmed) {
						delivery.status = 'canceling'
						this.singleCancelLoading = true
						restApi
							.delete(`/campaigns/delivery/` + delivery.id)
							.then(() => {
								this.getDeliveryObjects()
								this.getDeliveryCanceledCount()
								this.singleCancelLoading = false
							})
							.catch(error => {
								this.$swal.fire({
									icon: 'error',
									title: handleErrors(error, ''),
									timer: 6000,
								})
								this.singleCancelLoading = false
							})
					}
				})
		},

		// Send campaign button available
		sendButtonDisable() {
			let disabled = true
			if (this.campaign.type === 'letter') {
				if (this.campaign.letter_type == 'default') {
					if (this.campaign.letter_pdf !== '' && this.campaign.letter_pdf !== null) {
						disabled = false
					} else {
						disabled = true
					}
				} else {
					if (this.campaign.letter_pattern !== '' && this.campaign.letter_pattern !== null) {
						disabled = false
					} else {
						disabled = true
					}
				}

				if (
					this.campaign.name !== '' &&
					this.campaign.sender !== '' &&
					this.campaign.letter_color !== null &&
					this.campaign.letter_double_sided !== null &&
					this.campaign.letter_address_placement !== '' &&
					this.campaign.mail_type &&
					((this.campaign.letter_pdf !== '' && this.campaign.letter_pdf !== null) || this.campaign.letter_pattern == 'custom')
				) {
					disabled = false
				}
			} else {
				if (
					this.campaign.name !== '' &&
					this.campaign.name != 'null' &&
					this.campaign.name !== null &&
					this.campaign.sender !== '' &&
					this.campaign.postcard_size !== '' &&
					this.campaign.postcard_back !== '' &&
					this.campaign.postcard_back !== null &&
					this.campaign.postcard_front !== '' &&
					this.campaign.postcard_front !== null &&
					this.campaign.mail_type
				) {
					disabled = false
				}
			}
			return disabled
		},

		// Cancel campaign
		cancelCampaign() {
			this.$swal
				.fire({
					title: 'Are you sure want to cancel this campaign?',
					text: `All deliveries will be canceled if get to them in time!`,
					icon: 'warning',
					position: 'center',
					showCancelButton: true,
					showConfirmButton: true,
					confirmButtonColor: '#3085d6',
					cancelButtonColor: '#d33',
					confirmButtonText: 'Yes, cancel it!',
					toast: false,
					timer: 0,
				})
				.then(result => {
					if (result.isConfirmed) {
						this.campaignCancelLoading = true
						restApi
							.post(`/campaigns/cancel/` + this.campaign.id)
							.then(() => {
								this.getCampaign()
								this.campaignCancelLoading = false
							})
							.catch(error => {
								this.$swal.fire({
									icon: 'error',
									title: handleErrors(error, ''),
									timer: 6000,
								})
								this.campaignCancelLoading = false
							})
					}
				})
		},

		// Modal when sending live campaign
		openLiveModal() {
			this.$sendLiveModal.show()
			this.estimateCampaignCosts(this.campaign.municipalities_count)
			this.loadingLivePreview = true
			// Save first: the test postcard is rendered from the saved template patterns.
			this.updateCampaign()
				.then(saved => (saved ? restApi.post(`/campaigns/send-test/${this.campaign.id}`) : { notSaved: true }))
				.then(({ data, notSaved }) => {
					if (!data || !data.data) {
						this.$sendLiveModal.hide()
						this.loadingLivePreview = false
						// A failed save already showed its own error.
						if (notSaved) return
						this.$swal.fire({ icon: 'error', title: (data && data.error && data.error.message) || 'The test postcard could not be sent.', timer: 6000 })
						return
					}
					setTimeout(() => {
						this.liveResponseData = data
						this.loadingLivePreview = false
					}, 10000)
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: this.apiErrorMessage(error),
						timer: 6000,
					})
					this.$sendLiveModal.hide()
					this.loadingLivePreview = false
				})
		},

		/**
		 *
		 */
		closeLiveModal() {
			restApi
				.delete(`/campaigns/delivery-test/` + this.liveResponseData.data.id)
				.then(() => {
					this.liveResponseData = {
						error: '',
						data: {
							id: '',
							url: '',
							thumbnail: '',
						},
					}
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
					this.singleCancelLoading = false
				})
		},

		/**
		 * Get Campaign Deliveries Count
		 * Function returs number of sent deliveries
		 */
		getCampaignDeliveriesCount() {
			this.params = {
				campaign_id: this.$route.params.id,
			}
			restApi
				.get('/deliveries/count', { params: this.params })
				.then(({ data }) => {
					this.sentDeliveries = data
					this.getCampaign(true)
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Create carnivore
		createCarnivore() {
			if (this.campaign.municipalities.length > 0) {
				this.newCarnivore.dataType = 'custom'
				this.newCarnivore.municipalities = this.campaign.municipalities
				this.newCarnivore.municipalities_count = this.campaign.municipalities.length
			} else {
				this.newCarnivore.filters = JSON.stringify(this.campaign.filters)
				this.newCarnivore.municipalities_count = this.pagination.total
			}
			// Store new carnivore
			restApi
				.post(`/carnivores`, this.newCarnivore)
				.then(({ data }) => {
					this.$carnivoreModal.hide()
					this.$router.push(`/carnivores/edit/${data.id}`)
					this.$swal.fire({
						icon: 'success',
						title: 'Carnivore successfully created!',
						timer: 4000,
					})
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		updateCampaignMunicipalities() {
			this.campaign.municipalities_count = this.campaign.municipalities.length
			restApi
				.put(`/campaigns/${this.$route.params.id}`, this.campaign)
				.then(({ data }) => {
					delete data.changes.updated_at
					delete data.changes.filters

					if (this.campaign.municipalities.length > 0) {
						this.getMunicipalitiesByIds()
					} else {
						this.getMunicipalities()
					}
					this.getMunicipalitiesStatuses()

					this.$swal.fire({
						icon: 'success',
						title: 'Municipalities list successfully updated!',
						timer: 3000,
					})
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		/**
		 * Preview PDF
		 * Function generate PDF preview from pattern that is created
		 */
		previewPdf() {
			let item = null
			if (this.deliveryObjects.length > 0) {
				item = this.deliveryObjects[0]
			}
			let data = {
				campaign: this.campaign,
				item: item,
			}
			restApi
				.post(`/campaigns/pdf-preview`, data)
				.then(({ data }) => {
					this.customLetterPdfUrl = data
					this.displayPreviewPdf = true
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		/**
		 * Generate Pattern Variables
		 * Function generates variables for PDF pattern for campaign Letter
		 */
		generatePatternVariables() {
			// Short link / QR code first (rendered specially by the API); the raw tracking columns are internal.
			this.patternVariables = ['{{ $unique_url }}', '{{ $unique_url_qr }}']
			const internal = ['unique_url', 'unique_url_token', 'unique_url_visited_at', 'unique_url_visit_info', 'unique_url_visits', 'unique_url_last_visited_at', 'p_front', 'p_back']
			for (const property in this.deliveryObjects[0]) {
				if (internal.includes(property)) continue
				if (property != 'client' && property != 'municipality') this.patternVariables.push('{{ $' + property + ' }}')
				if (property == 'client') {
					for (const clientProperty in this.deliveryObjects[0].client) {
						this.patternVariables.push('{{ $' + property + '.' + clientProperty + ' }}')
					}
				}
				if (property == 'municipality') {
					for (const municipalityProperty in this.deliveryObjects[0].municipality) {
						this.patternVariables.push('{{ $' + property + '.' + municipalityProperty + ' }}')
					}
				}
			}
		},

		// Set municipality for new Delivery Object
		setMunicipality(municipality) {
			this.dObjectNew.name = municipality.full_name
			this.dObjectNew.state = municipality.state
			this.dObjectNew.city = municipality.city
			this.dObjectNew.address = municipality.address_street
			this.dObjectNew.zip = municipality.zip
			this.dObjectNew.municipality_id = municipality.id
		},

		// Set client for new Delivery Object
		setClient(client) {
			let arrayMailing = client.address_mailing.split(',')
			if (arrayMailing.length == 4) {
				this.dObjectNew.state = arrayMailing[2].trim()
				this.dObjectNew.city = arrayMailing[1].trim()
				this.dObjectNew.address = arrayMailing[0].trim()
				this.dObjectNew.zip = arrayMailing[3].trim()
			} else {
				this.dObjectNew.state = client.state_abb
				this.dObjectNew.city = client.city
				this.dObjectNew.address = client.address
				this.dObjectNew.zip = client.zip
			}
			this.dObjectNew.name = client.name
			this.dObjectNew.municipality_id = client.municipality_id
			this.dObjectNew.client_id = client.id
		},

		// Estimate campaign costs
		estimateCampaignCosts(number) {
			let total = 0
			let additionalPageCost = 0
			let postcard4x6 = 0.513
			let postcard6x9 = 0.554
			let postcard6x11 = 0.781
			let postcard4x6Standard = 0.513
			let postcard6x9Standard = 0.554
			let postcard6x11Standard = 0.781
			let blackLetter = 0.588
			let additionalBlackPage = 0.1
			let colorLetter = 0.761
			let additionalColorPage = 0.2
			let blacStandardLetter = 0.548
			let additionalBlackStandardPage = 0.1
			let colorStandardLetter = 0.578
			let additionalColorStandardPage = 0.2
			// Check if campaign is letter
			if (this.campaign.type == 'letter') {
				if (this.campaign.letter_color) {
					if (this.campaign.mail_type == 'usps_first_class') {
						total = total + colorLetter
						additionalPageCost = additionalPageCost + additionalColorPage
					} else {
						total = total + colorStandardLetter
						additionalPageCost = additionalPageCost + additionalColorStandardPage
					}
				} else {
					if (this.campaign.mail_type == 'usps_first_class') {
						total = total + blackLetter
						additionalPageCost = additionalPageCost + additionalBlackPage
					} else {
						total = total + blacStandardLetter
						additionalPageCost = additionalPageCost + additionalBlackStandardPage
					}
				}
				if (this.campaign.letter_extra_service == 'certified') {
					total = total + 5.75
				}
				if (this.campaign.letter_extra_service == 'certified_return_receipt') {
					total = total + 5.75 + 2.2
				}
				let pages = this.campaign.letter_pages_count
				if (this.campaign.letter_double_sided) {
					pages = Math.ceil(pages / 2)
				}

				let additionalPagesCost = (pages - 1) * additionalPageCost
				total = total + additionalPagesCost
				this.costsEstimated = number * total
				this.costsEstimated = this.costsEstimated.toFixed(2)
			} else {
				if (this.campaign.postcard_size == '4x6') {
					if (this.campaign.mail_type == 'usps_first_class') {
						total = postcard4x6
					} else {
						total = postcard4x6Standard
					}
				}
				if (this.campaign.postcard_size == '6x9') {
					if (this.campaign.mail_type == 'usps_first_class') {
						total = postcard6x9
					} else {
						total = postcard6x9Standard
					}
				}
				if (this.campaign.postcard_size == '6x11') {
					if (this.campaign.mail_type == 'usps_first_class') {
						total = postcard6x11
					} else {
						total = postcard6x11Standard
					}
				}
				this.costsEstimated = number * total
				this.costsEstimated = this.costsEstimated.toFixed(2)
			}
		},

		clearSearch() {
			this.deliveryObjectSearch = ''
			this.getDeliveryObjects()
		},
	},

	watch: {
		page: function() {
			if (this.campaign.status == 'sent' || this.campaign.status == 'canceled') {
				this.getDeliveryObjects()
			}
		},
		deliveryObjects: function() {
			if (this.deliveryObjects.length > 0) {
				this.generatePatternVariables()
			}
		},
		deliveryObjectsCount: function() {
			this.estimateCampaignCosts(this.totalSentDeliveryObjects)
		},
		deliveryErrorsCount: function() {
			this.estimateCampaignCosts(this.totalSentDeliveryObjects)
		},
		deliveryCanceledCount: function() {
			this.estimateCampaignCosts(this.totalSentDeliveryObjects)
		},
	},
}
</script>
<style scoped>
.letter-settings {
	position: relative;
}
.letter-settings-overlay {
	position: absolute;
	background-color: red;
	top: 0px;
	left: -5px;
	width: calc(100% + 10px);
	height: 100%;
	border-radius: 5px;
	background: rgba(255, 255, 255, 0.1); /* Providnost i mutnost */
	z-index: 1000; /* Podesite z-index prema potrebi */
	display: flex;
	justify-content: center;
	align-items: center;
	backdrop-filter: blur(3px); /* Dodajte zamagljivanje */
}

.postcard-settings {
	position: relative;
}

.postcard-settings-overlay {
	position: absolute;
	background-color: red;
	top: 0px;
	left: -5px;
	width: calc(100% + 10px);
	height: 100%;
	border-radius: 5px;
	background: rgba(255, 255, 255, 0.1); /* Providnost i mutnost */
	z-index: 1000; /* Podesite z-index prema potrebi */
	display: flex;
	justify-content: center;
	align-items: center;
	backdrop-filter: blur(3px); /* Dodajte zamagljivanje */
}
.test-mode-settings {
	position: relative;
}
.test-mode-settings-overlay {
	position: absolute;
	background-color: red;
	top: 0px;
	left: -5px;
	width: calc(100% + 10px);
	height: 100%;
	border-radius: 5px;
	background: rgba(255, 255, 255, 0.1); /* Providnost i mutnost */
	z-index: 1000; /* Podesite z-index prema potrebi */
	display: flex;
	justify-content: center;
	align-items: center;
	backdrop-filter: blur(3px); /* Dodajte zamagljivanje */
}

.live-mode-settings {
	position: relative;
}

.live-mode-settings-overlay {
	position: absolute;
	background-color: red;
	top: 0px;
	left: -5px;
	width: calc(100% + 10px);
	height: 100%;
	border-radius: 5px;
	background: rgba(255, 255, 255, 0.1); /* Providnost i mutnost */
	z-index: 1000; /* Podesite z-index prema potrebi */
	display: flex;
	justify-content: center;
	align-items: center;
	backdrop-filter: blur(3px); /* Dodajte zamagljivanje */
}

.selected-filter {
	border: 2px solid var(--bs-primary);
}
</style>
